import { useState, type ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * Estructura de la aplicación (movida a `packages/ui` en 02A): sidebar agrupada por áreas, barra
 * superior en móvil, skip link y bloque de usuario. La NAVEGACIÓN ES DATOS: quien la usa la deriva
 * de los permisos y módulos del Principal (nunca de una lista fija) y pasa cómo dibujar enlaces
 * (`renderLink`), así la librería no depende del enrutador.
 */

export interface ShellNavItem {
  id: string;
  label: string;
  /** Destino. Sin `href` (o con `soon`), el ítem se muestra deshabilitado. */
  href?: string;
  icon?: ReactNode;
  /** Activo solo en la ruta exacta (p. ej. Inicio). */
  end?: boolean;
  /** Etiqueta breve a la derecha (p. ej. "MFA" pendiente). */
  badge?: ReactNode;
  /** Llega en otra fase: visible pero deshabilitado ("Próximamente · 02B"). */
  soon?: string;
  /** (05B2) Se abre en una pestaña propia (p. ej. el Inbox): ícono y texto lo anuncian. */
  newTab?: boolean;
}

export interface ShellNavGroup {
  id: string;
  label: string;
  items: readonly ShellNavItem[];
}

export interface ShellLinkProps {
  href: string;
  className: string;
  children: ReactNode;
  onClick: () => void;
  'aria-current'?: 'page';
  /** (05B2) `_blank` + `noopener` para los destinos en pestaña propia. */
  target?: '_blank';
  rel?: string;
}

export interface AppShellProps {
  brand: ReactNode;
  nav: readonly ShellNavGroup[];
  currentPath: string;
  renderLink: (props: ShellLinkProps) => ReactNode;
  /** Acciones junto a la marca en escritorio (p. ej. campana de notificaciones). */
  sidebarActions?: ReactNode;
  /** Acciones de la barra superior en móvil. */
  topbarActions?: ReactNode;
  /** Pie de la sidebar (bloque de usuario). */
  footer?: ReactNode;
  children: ReactNode;
}

export function isNavItemActive(item: ShellNavItem, path: string): boolean {
  if (!item.href || item.soon) return false;
  if (item.end || item.href === '/') return path === item.href;
  return path === item.href || path.startsWith(`${item.href}/`);
}

export function AppShell({
  brand,
  nav,
  currentPath,
  renderLink,
  sidebarActions,
  topbarActions,
  footer,
  children,
}: AppShellProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[256px_1fr]">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-md focus:bg-brasa focus:px-3 focus:py-2 focus:text-carbon"
      >
        Ir al contenido
      </a>

      {/* Barra superior móvil */}
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-escoria bg-hierro/95 px-4 backdrop-blur lg:hidden">
        {brand}
        <div className="flex items-center gap-1">
          {topbarActions}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="nav-principal"
            className="grid size-10 place-items-center rounded-md text-ceniza hover:bg-forja hover:text-hueso"
          >
            <svg
              aria-hidden
              viewBox="0 0 24 24"
              className="size-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              {open ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
            <span className="sr-only">{open ? 'Cerrar menú' : 'Abrir menú'}</span>
          </button>
        </div>
      </div>

      <aside
        id="nav-principal"
        className={cn(
          'border-escoria bg-hierro lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col lg:border-r',
          open ? 'flex flex-col border-b' : 'hidden',
        )}
      >
        <div className="hidden h-16 items-center justify-between pl-5 pr-3 lg:flex">
          {brand}
          {sidebarActions}
        </div>
        <nav
          aria-label="Navegación principal"
          className="flex-1 space-y-6 overflow-y-auto px-3 py-4 lg:py-2"
        >
          {nav.map((group) => (
            <div key={group.id}>
              <p className="px-2 pb-2 text-[11px] font-semibold uppercase font-condensed text-ceniza-2">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isNavItemActive(item, currentPath);
                  if (!item.href || item.soon)
                    return (
                      <li key={item.id}>
                        <span
                          aria-disabled="true"
                          className="flex h-10 cursor-not-allowed items-center gap-3 rounded-md px-2 text-sm text-ceniza-2 lg:h-9 [&_svg]:size-4"
                        >
                          {item.icon}
                          <span className="flex-1">{item.label}</span>
                          {item.soon ? (
                            <span className="font-mono text-[10.5px] text-ceniza-2/80">
                              <span className="sr-only">Próximamente, fase </span>
                              {item.soon}
                            </span>
                          ) : null}
                        </span>
                      </li>
                    );
                  return (
                    <li key={item.id}>
                      {renderLink({
                        href: item.href,
                        onClick: () => setOpen(false),
                        'aria-current': active ? 'page' : undefined,
                        ...(item.newTab ? { target: '_blank' as const, rel: 'noopener' } : {}),
                        className: cn(
                          'relative flex h-10 items-center gap-3 rounded-md px-2 text-sm transition-colors lg:h-9 [&_svg]:size-4',
                          active
                            ? 'bg-forja font-medium text-hueso [&_svg]:text-brasa'
                            : 'text-ceniza hover:bg-forja hover:text-hueso',
                        ),
                        children: (
                          <>
                            {active ? (
                              // Firma: el destino activo está "al rojo".
                              <span
                                aria-hidden
                                className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-linear-to-b from-brasa to-brasa-honda shadow-(--shadow-ember)"
                              />
                            ) : null}
                            {item.icon}
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.badge}
                            {item.newTab ? (
                              <svg
                                aria-hidden
                                viewBox="0 0 24 24"
                                className="!size-3.5 text-ceniza-2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              </svg>
                            ) : null}
                            {item.newTab ? (
                              <span className="sr-only"> (se abre en otra pestaña)</span>
                            ) : null}
                          </>
                        ),
                      })}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
        {footer}
      </aside>

      <main id="contenido" className="min-w-0">
        {children}
      </main>
    </div>
  );
}
